import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getPublicClasses, getClassById, joinClass, getMyBookings } from '../../api/classes';

// ──────────────────────────────────────────────
// Async Thunks
// ──────────────────────────────────────────────

export const fetchPublicClasses = createAsyncThunk(
  'classes/fetchPublicClasses',
  async (_, { rejectWithValue }) => {
    try {
      return await getPublicClasses();
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch classes' });
    }
  }
);

export const fetchClassById = createAsyncThunk(
  'classes/fetchClassById',
  async (id, { rejectWithValue }) => {
    try {
      return await getClassById(id);
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch class details' });
    }
  }
);

export const bookClass = createAsyncThunk(
  'classes/bookClass',
  async (id, { rejectWithValue }) => {
    try {
      return await joinClass(id);
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to book class' });
    }
  }
);

export const fetchMyBookings = createAsyncThunk(
  'classes/fetchMyBookings',
  async (_, { rejectWithValue }) => {
    try {
      return await getMyBookings();
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch bookings' });
    }
  }
);

// ──────────────────────────────────────────────
// Slice
// ──────────────────────────────────────────────

const classesSlice = createSlice({
  name: 'classes',
  initialState: {
    classes: [],
    currentClass: null,
    myBookings: [],
    loading: {
      list: false,
      detail: false,
      booking: false,
      myBookings: false,
    },
    error: {
      list: null,
      detail: null,
      booking: null,
      myBookings: null,
    },
    filters: {
      type: '', // 'one_to_one' | 'group' | ''
      language: '',
      search: '',
    },
  },
  reducers: {
    setClassFilter: (state, action) => {
      const { key, value } = action.payload;
      state.filters[key] = value;
    },
    clearClassFilters: (state) => {
      state.filters = { type: '', language: '', search: '' };
    },
    clearClassErrors: (state) => {
      state.error = { list: null, detail: null, booking: null, myBookings: null };
    },
  },
  extraReducers: (builder) => {
    builder
      // ── fetchPublicClasses ──
      .addCase(fetchPublicClasses.pending, (state) => {
        state.loading.list = true;
        state.error.list = null;
      })
      .addCase(fetchPublicClasses.fulfilled, (state, action) => {
        state.loading.list = false;
        state.classes = action.payload.data?.classes || (Array.isArray(action.payload.data) ? action.payload.data : (Array.isArray(action.payload) ? action.payload : []));
      })
      .addCase(fetchPublicClasses.rejected, (state, action) => {
        state.loading.list = false;
        state.error.list = action.payload?.message || 'Failed to fetch classes';
      })
      // ── fetchClassById ──
      .addCase(fetchClassById.pending, (state) => {
        state.loading.detail = true;
        state.error.detail = null;
      })
      .addCase(fetchClassById.fulfilled, (state, action) => {
        state.loading.detail = false;
        state.currentClass = action.payload.data || null;
      })
      .addCase(fetchClassById.rejected, (state, action) => {
        state.loading.detail = false;
        state.error.detail = action.payload?.message || 'Failed to fetch class';
      })
      // ── bookClass ──
      .addCase(bookClass.pending, (state) => {
        state.loading.booking = true;
        state.error.booking = null;
      })
      .addCase(bookClass.fulfilled, (state) => {
        state.loading.booking = false;
      })
      .addCase(bookClass.rejected, (state, action) => {
        state.loading.booking = false;
        state.error.booking = action.payload?.message || 'Failed to book class';
      })
      // ── fetchMyBookings ──
      .addCase(fetchMyBookings.pending, (state) => {
        state.loading.myBookings = true;
        state.error.myBookings = null;
      })
      .addCase(fetchMyBookings.fulfilled, (state, action) => {
        state.loading.myBookings = false;
        state.myBookings = action.payload.data || [];
      })
      .addCase(fetchMyBookings.rejected, (state, action) => {
        state.loading.myBookings = false;
        state.error.myBookings = action.payload?.message || 'Failed to fetch bookings';
      });
  },
});

export const { setClassFilter, clearClassFilters, clearClassErrors } = classesSlice.actions;

export default classesSlice.reducer;
